'use client';

import { type RefObject, useCallback, useEffect, useLayoutEffect } from 'react';

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
}

/**
 * Keeps a fixed popover next to its anchor: below it when it fits (or when
 * there is more room below), else above; right edges aligned; always inside
 * the viewport. The popover lives in the top layer, so no scroll container or
 * dialog clips it — it only has to follow the anchor.
 */
export function usePopoverPosition(
    anchorRef: RefObject<HTMLElement | null>,
    popoverRef: RefObject<HTMLElement | null>,
) {
    const update = useCallback(() => {
        const anchor = anchorRef.current;
        const popover = popoverRef.current;
        if (!anchor || !popover) return;

        const gap = parseFloat(getComputedStyle(popover).getPropertyValue('--datepicker-offset')) || 0;
        const rect = anchor.getBoundingClientRect();
        const { offsetHeight: height, offsetWidth: width } = popover;
        const roomBelow = window.innerHeight - rect.bottom;
        const below = roomBelow >= height + gap || roomBelow >= rect.top;
        const top = below ? rect.bottom + gap : rect.top - gap - height;

        popover.style.top = `${Math.round(clamp(top, gap, window.innerHeight - height - gap))}px`;
        popover.style.left = `${Math.round(clamp(rect.right - width, gap, window.innerWidth - width - gap))}px`;
    }, [anchorRef, popoverRef]);

    // Every render may change the popover's size (another month, another view).
    useLayoutEffect(update);

    useEffect(() => {
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, { capture: true, passive: true });

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, { capture: true });
        };
    }, [update]);
}
